<template>
    <ui-modal>
        <template #header>
            <h3 class="text-lg text-center mb-0">
                Podsumowanie aktywacji usługi ({{ `${processedShops.activated.length}/${shopsTotal}` }})
            </h3>
        </template>
        <div class="mb-3">
            Informujemy, że aktywacja usługi {{ drsService.name }} powiodła się
            w {{ processedShops.activated.length }} z {{ shopsTotal | plurals('sklepu', 'sklepów') }}.
            <span
                v-if="this.isLocationMappingProcess"
                v-text="'Powiązaliśmy ze sobą następujące sklepy między systemami EuroPlatform oraz Kaucyjni.pl.'"
            />
            <span v-else>
                W przypadku nieudanej aktywacji prosimy o kontakt
                na <a class="text-primary" href="mailto:pomoc@kaucyjni.pl">pomoc@kaucyjni.pl</a>.
                Skonfiguruj pakiet, aby otrzymać dostęp do zakładki System kaucyjny.
                Nie musisz robić tego teraz - konfiguracja jest również możliwa z poziomu zakładki System kaucyjny
            </span>
        </div>

        <template v-if="this.isLocationMappingProcess">
            <location-mapping-result-table />
        </template>

        <template v-else>
            <div v-for="shopId in shopIds" class="shop-row text-primary" :key="shopId">
                <a-icon
                    :icon="processedShops.activated.includes(shopId) ? 'Ok' : 'ErrorCircle'"
                    width="20"
                    class="mr-1"
                />
                <p class="mb-0">
                    <strong v-text="$store.getters.getMerchantByLocationId(shopId).name" />,
                    {{ $store.getters.getMerchantByLocationId(shopId).fullAddress }}
                </p>
            </div>
        </template>

        <template v-if="this.isLocationMappingProcess" #footer>
            <the-btn
                text="Zamknij"
                class="btn-outline-custom"
                @click="closeModalAndClearLocationMappingState"
            />
            <the-btn
                :text="nextStepText"
                class="btn-secondary"
                :disabled="isCompanyStatusLoading"
                @click="goToDrs"
            />
        </template>

        <template v-else #footer>
            <the-btn
                class="btn-outline-custom"
                text="Przejdź do panelu System Kaucyjny"
                @click="goToDrs"
            />
            <the-btn
                :disabled="!processedShops.activated.length"
                class="btn-secondary"
                text="Przejdź do konfiguracji pakietu"
                :icon="isLoader ? 'Spinner' : ''"
                :loading-status="isLoader ? 'loading' : 'idle'"
                @click="configure"
            />
        </template>
    </ui-modal>
</template>

<script>
import { registerPageModule } from '@/utils/store'
import DrsStore, { KPL_SERVICE_CODE } from '@/store/modules/drs/drs.module'
import DrsTariffs, { CONFIG_MODES } from '@/store/modules/drs/tariffs.module'
import { COMPANY_STATUSES } from '@/utils/constants/drs/company-statuses'

export default {
    data: () => ({
        drsShops: [],
        isLoader: false,
        isCompanyStatusLoading: true
    }),

    computed: {
        shopIds() {
            // Shops that were in the process of activation
            return this.$store.getters.getToActiveServiceShopIds
        },

        shopsTotal() {
            return this.shopIds.length
        },

        processedShops() {
            const activated = []
            const failed = []
            const drsShops = this.drsShops.filter(shop => this.shopIds.includes(shop.entityData.id))

            for (const shop of drsShops) {
                if (shop.status === 'active') {
                    activated.push(shop.entityData.id)
                } else {
                    failed.push(shop.entityData.id)
                }
            }
            return { activated, failed }
        },

        userId() {
            return this.$store.getters.getUser.id
        },

        serviceCode() {
            return this.$store.getters.modalState.args.serviceCode
        },

        drsService() {
            return this.$store.getters.getServiceByCode(this.serviceCode)
        },

        isLocationMappingProcess() {
            return this.$store.getters.modalState.args.isLocationMappingProcess
        },

        isCompanyVerified() {
            return this.$store.getters['drs/companyStatus'] === COMPANY_STATUSES.VERIFIED
        },

        hasMappedSubscriptionChanges() {
            return this.$store.getters.modalState.args.hasMappedSubscriptionChanges
        },

        authorizationPaymentUrl() {
            return this.$store.getters.modalState.args.authorizationPaymentUrl
        },

        shouldGoToTariffConfiguration() {
            return this.isLocationMappingProcess && this.hasMappedSubscriptionChanges && !this.isCompanyVerified
        },

        nextStepText() {
            if (this.authorizationPaymentUrl) {
                return 'Przejdź do autoryzacji'
            }

            if (this.shouldGoToTariffConfiguration) {
                return 'Przejdź do aktualizacji pakietu'
            }

            return 'Przejdź do panelu System kaucyjny'
        }
    },

    methods: {
        async configure() {
            await this.loadDrsData()
            await this.$store.dispatch('drsTariffs/setConfigMode', CONFIG_MODES.CREATE)

            this.$store.dispatch('toggleModal', false)
            this.$nextTick(() => {
                this.$store.dispatch('openModal', {
                    componentPath: 'drs/configuration/SelectTariff'
                })
            })
        },

        async loadDrsData() {
            this.isLoader = true

            await registerPageModule(this.$store, DrsStore, 'drs')
            await registerPageModule(this.$store, DrsTariffs, 'drsTariffs')

            await this.$store.dispatch('drs/loadActiveServiceEpLocations')
            await this.$store.dispatch('drs/loadDataAndCheckPermission')

            this.isLoader = false
        },

        async goToDrs() {
            if (this.authorizationPaymentUrl) {
                this.$store.dispatch('openModal', {
                    componentPath: 'drs/service/AuthorizationPaymentWarning',
                    args: {
                        paymentUrl: this.authorizationPaymentUrl
                    }
                })
                return
            }

            if (this.shouldGoToTariffConfiguration) {
                await this.$store.dispatch('drsTariffs/setConfigMode', CONFIG_MODES.MAPPING)
                this.$store.dispatch('openModal', {
                    componentPath: 'drs/configuration/ConfigurationSummary'
                })
                return
            }

            this.$emit('close-modal')
            this.$router.push({ name: 'drs' })
        },

        async fetchDrsShops() {
            try {
                const { data, status } = await this.$store.$api.get(
                    `subscriptions_services/${this.userId}/services/${this.drsService.id}`,
                    {
                        params: {
                            subscriptionsWithEntityData: true
                        }
                    }
                )

                if (status !== 200 || !data?.data) {
                    throw new Error('Failed to fetch DRS shops data')
                }

                this.drsShops = data.data.subscriptions || []
            } catch (error) {
                console.error('Failed to fetch DRS shops:', error)
            }
        },

        async activateKplService() {
            if (!this.processedShops.activated.length) return

            try {
                const [kplService, locationsToActivate] = await Promise.all([
                    this.fetchKplService(),
                    this.getLocationsToActivate()
                ])

                if (!kplService || !locationsToActivate.length) return

                // activate KPL service
                await this.$store.$api.patch(
                    `drs/api/proxy/subscriptions/users/[drs_user_id]/services/${kplService.id}/activate`,
                    {
                        activation_level: kplService.level.toUpperCase(),
                        objectIds: locationsToActivate.map(loc => loc.id)
                    },
                    {
                        headers: { source: 'PD' }
                    }
                )
            } catch (error) {
                console.error('Failed to activate KPL service:', error)
            }
        },

        async fetchKplService() {
            const { data } = await this.$store.$api.get(
                'drs/api/proxy/subscriptions/[drs_user_id]/services'
            )

            return data?.services?.find(
                el => el.code === KPL_SERVICE_CODE
            )
        },

        async getLocationsToActivate() {
            const { data } = await this.$store.$api.get(
                'drs/api/proxy/users/[drs_user_id]/extendedData?includes=locations'
            )

            const locations = data?.locations || []

            return locations.filter(loc =>
                this.processedShops.activated.includes(loc.ep_location_id)
            )
        },

        closeModalAndClearLocationMappingState() {
            this.$store.dispatch('resetDrsLocationMappingState')
            this.$emit('close-modal')
        }

    },

    async created() {
        const requests = [this.fetchDrsShops()]

        if (this.isLocationMappingProcess) {
            requests.push(this.$store.dispatch('drs/getCurrentTransaction'))
        }

        await Promise.all(requests)
        this.isCompanyStatusLoading = false
        this.activateKplService()
    }
}
</script>

<style lang="scss">
.shop-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1.25rem;
    border: 1px solid var(--color-gray-300);
    border-radius: 4px;

    + .shop-row {
        margin-top: 0.5rem;
    }
}
</style>